import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { useToast } from '../hooks/use-toast';
import { 
  FileText, Plus, AlertTriangle, CheckCircle, Shield, 
  Copy, Trash2, Edit, ChevronDown, ChevronUp, Target,
  Download, Search, Filter, Sparkles, RefreshCw, Eye,
  FileCheck, History, Grid3X3, Loader2, X, Brain
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const JHABuilderPage = () => {
  const [jhas, setJhas] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [industryTemplates, setIndustryTemplates] = useState([]);
  const [controlHierarchy, setControlHierarchy] = useState([]);
  const [riskMatrix, setRiskMatrix] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedJHA, setSelectedJHA] = useState(null);
  const [editingJHA, setEditingJHA] = useState(null);
  const [expandedSteps, setExpandedSteps] = useState({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [showRiskMatrix, setShowRiskMatrix] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const { toast } = useToast();

  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterRisk, setFilterRisk] = useState('all');

  const [formData, setFormData] = useState({
    title: '',
    job_description: '',
    department: '',
    location: '',
    project_name: '',
    prepared_by: '',
    task_steps: [],
    required_training: [],
    emergency_procedures: '',
    ppe_summary: []
  });

  const fetchData = useCallback(async () => {
    try {
      const [jhasRes, templatesRes, industryRes, hierarchyRes, statsRes, matrixRes] = await Promise.all([
        axios.get(`${API}/api/jha-builder`),
        axios.get(`${API}/api/jha-builder/templates`),
        axios.get(`${API}/api/jha-builder/templates/industries`).catch(() => ({ data: { industries: [] } })),
        axios.get(`${API}/api/jha-builder/control-hierarchy`),
        axios.get(`${API}/api/jha-builder/stats/summary`),
        axios.get(`${API}/api/jha-builder/risk-matrix`).catch(() => ({ data: null }))
      ]);
      setJhas(jhasRes.data.jhas || []);
      setTemplates(templatesRes.data.templates || []);
      setIndustryTemplates(industryRes.data.industries || []);
      setControlHierarchy(hierarchyRes.data.hierarchy || []);
      setStats(statsRes.data);
      setRiskMatrix(matrixRes.data);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load data', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // AI Hazard Suggestions
  const getAiHazardSuggestions = async (stepIndex) => {
    if (!formData.job_description && !formData.task_steps[stepIndex]?.task_description) {
      toast({ title: 'Info', description: 'Please enter a job or task description first' });
      return;
    }

    setAiLoading(true);
    try {
      const response = await axios.post(`${API}/api/jha-builder/ai/suggest-hazards`, {
        job_description: formData.job_description,
        task_step: formData.task_steps[stepIndex]?.task_description || '',
        industry: formData.department
      });

      setAiSuggestions(response.data.suggestions || []);
      toast({ 
        title: response.data.ai_powered ? 'AI Analysis Complete' : 'Suggestions Ready',
        description: `Found ${response.data.suggestions?.length || 0} potential hazards`
      });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to get AI suggestions', variant: 'destructive' });
    } finally {
      setAiLoading(false);
    }
  };

  const applyAiSuggestion = (stepIndex, suggestion) => {
    const updated = [...formData.task_steps];
    if (!updated[stepIndex]) return;
    
    updated[stepIndex].hazards.push({
      hazard: suggestion.hazard,
      consequences: suggestion.consequences,
      severity: suggestion.severity,
      likelihood: suggestion.likelihood,
      controls: suggestion.suggested_controls || []
    });
    setFormData({ ...formData, task_steps: updated });
    setAiSuggestions(aiSuggestions.filter(s => s.hazard !== suggestion.hazard));
    toast({ title: 'Added', description: 'Hazard added to step' });
  };

  const handleCreateJHA = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/api/jha-builder`, formData);
      toast({ title: 'Success', description: 'JHA created successfully!' });
      setShowCreateForm(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to create JHA', variant: 'destructive' });
    }
  };

  const handleUpdateJHA = async (e) => {
    e.preventDefault();
    if (!editingJHA) return;
    
    try {
      await axios.put(`${API}/api/jha-builder/${editingJHA.id}`, formData);
      toast({ title: 'Success', description: 'JHA updated successfully!' });
      setEditingJHA(null);
      setShowCreateForm(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to update JHA', variant: 'destructive' });
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      job_description: '',
      department: '',
      location: '',
      project_name: '',
      prepared_by: '',
      task_steps: [],
      required_training: [],
      emergency_procedures: '',
      ppe_summary: []
    });
    setAiSuggestions([]);
    setEditingJHA(null);
  };

  const loadTemplate = (template) => {
    setFormData({
      ...formData,
      title: template.title,
      task_steps: template.task_steps.map((step, idx) => ({
        ...step,
        step_number: idx + 1,
        hazards: step.hazards.map(h => ({
          ...h,
          controls: h.controls || []
        }))
      })),
      ppe_summary: template.ppe_summary || []
    });
    toast({ title: 'Template Loaded', description: `Loaded ${template.title}` });
  };

  const editJHA = (jha) => {
    setEditingJHA(jha);
    setFormData({
      title: jha.title || '',
      job_description: jha.job_description || '',
      department: jha.department || '',
      location: jha.location || '',
      project_name: jha.project_name || '',
      prepared_by: jha.prepared_by || '',
      task_steps: jha.task_steps || [],
      required_training: jha.required_training || [],
      emergency_procedures: jha.emergency_procedures || '',
      ppe_summary: jha.ppe_summary || []
    });
    setSelectedJHA(null);
    setShowCreateForm(true);
  };

  const deleteJHA = async (jhaId) => {
    try {
      await axios.delete(`${API}/api/jha-builder/${jhaId}`);
      toast({ title: 'Deleted', description: 'JHA has been deleted' });
      setShowDeleteConfirm(null);
      setSelectedJHA(null);
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete JHA', variant: 'destructive' });
    }
  };

  const exportPDF = async (jhaId) => {
    try {
      const response = await axios.get(`${API}/api/jha-builder/${jhaId}/export/pdf`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `JHA_${jhaId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast({ title: 'Downloaded', description: 'PDF exported successfully' });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to export PDF', variant: 'destructive' });
    }
  };

  const addTaskStep = () => {
    setFormData({
      ...formData,
      task_steps: [...formData.task_steps, {
        step_number: formData.task_steps.length + 1,
        task_description: '',
        hazards: [],
        ppe_required: [],
        tools_equipment: [],
        special_precautions: ''
      }]
    });
  };

  const updateTaskStep = (index, field, value) => {
    const updated = [...formData.task_steps];
    updated[index] = { ...updated[index], [field]: value };
    setFormData({ ...formData, task_steps: updated });
  };

  const removeTaskStep = (index) => {
    const updated = formData.task_steps.filter((_, i) => i !== index);
    updated.forEach((step, i) => step.step_number = i + 1);
    setFormData({ ...formData, task_steps: updated });
  };

  const addHazardToStep = (stepIndex) => {
    const updated = [...formData.task_steps];
    updated[stepIndex].hazards.push({
      hazard: '',
      consequences: '',
      severity: 'medium',
      likelihood: 'possible',
      controls: []
    });
    setFormData({ ...formData, task_steps: updated });
  };

  const updateHazard = (stepIndex, hazardIndex, field, value) => {
    const updated = [...formData.task_steps];
    updated[stepIndex].hazards[hazardIndex] = {
      ...updated[stepIndex].hazards[hazardIndex],
      [field]: value
    };
    setFormData({ ...formData, task_steps: updated });
  };

  const removeHazard = (stepIndex, hazardIndex) => {
    const updated = [...formData.task_steps];
    updated[stepIndex].hazards = updated[stepIndex].hazards.filter((_, i) => i !== hazardIndex);
    setFormData({ ...formData, task_steps: updated });
  };

  const addControlToHazard = (stepIndex, hazardIndex) => {
    const updated = [...formData.task_steps];
    if (!updated[stepIndex].hazards[hazardIndex].controls) {
      updated[stepIndex].hazards[hazardIndex].controls = [];
    }
    updated[stepIndex].hazards[hazardIndex].controls.push({
      type: 'administrative',
      description: ''
    });
    setFormData({ ...formData, task_steps: updated });
  };

  const updateControl = (stepIndex, hazardIndex, controlIndex, field, value) => {
    const updated = [...formData.task_steps];
    updated[stepIndex].hazards[hazardIndex].controls[controlIndex] = {
      ...updated[stepIndex].hazards[hazardIndex].controls[controlIndex],
      [field]: value
    };
    setFormData({ ...formData, task_steps: updated });
  };

  const duplicateJHA = async (jhaId) => {
    try {
      await axios.post(`${API}/api/jha-builder/${jhaId}/duplicate`);
      toast({ title: 'Success', description: 'JHA duplicated' });
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to duplicate', variant: 'destructive' });
    }
  };

  const approveJHA = async (jhaId) => {
    try {
      await axios.post(`${API}/api/jha-builder/${jhaId}/approve?approved_by=Admin`);
      toast({ title: 'Approved', description: 'JHA has been approved' });
      fetchData();
      setSelectedJHA(null);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to approve', variant: 'destructive' });
    }
  };

  const getRiskColor = (level) => {
    switch (level) {
      case 'critical': return 'bg-red-100 text-red-800 border-red-300';
      case 'high': return 'bg-orange-100 text-orange-800 border-orange-300';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'low': return 'bg-green-100 text-green-800 border-green-300';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'bg-green-100 text-green-800';
      case 'draft': return 'bg-gray-100 text-gray-800';
      case 'reviewed': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const toggleStepExpand = (stepIndex) => {
    setExpandedSteps({ ...expandedSteps, [stepIndex]: !expandedSteps[stepIndex] });
  };

  // Filter JHAs
  const filteredJHAs = jhas.filter(jha => {
    const matchesSearch = !searchQuery || 
      jha.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      jha.jha_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      jha.prepared_by?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || jha.status === filterStatus;
    const matchesRisk = filterRisk === 'all' || jha.overall_risk_level === filterRisk;
    
    return matchesSearch && matchesStatus && matchesRisk;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">JHA/JSA Builder</h1>
          <p className="text-gray-600">Job Hazard Analysis - Document hazards and controls</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={() => window.location.href = '/ai-safety-generator'}
            className="border-purple-300 text-purple-700 hover:bg-purple-50"
          >
            <Sparkles className="w-4 h-4 mr-2" /> AI Generate
          </Button>
          <Button variant="outline" onClick={() => setShowRiskMatrix(true)}>
            <Grid3X3 className="w-4 h-4 mr-2" /> Risk Matrix
          </Button>
          <Button onClick={() => { resetForm(); setShowCreateForm(true); }} className="bg-blue-600 hover:bg-blue-700">
            <Plus className="w-4 h-4 mr-2" /> New JHA
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-3xl font-bold text-blue-600">{stats?.total_jhas || 0}</p>
              <p className="text-sm text-gray-600">Total JHAs</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-3xl font-bold text-green-600">{stats?.approved || 0}</p>
              <p className="text-sm text-gray-600">Approved</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-3xl font-bold text-gray-600">{stats?.draft || 0}</p>
              <p className="text-sm text-gray-600">Draft</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-3xl font-bold text-purple-600">{stats?.approval_rate || 0}%</p>
              <p className="text-sm text-gray-600">Approval Rate</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="flex justify-center gap-1">
                <span className="w-3 h-3 rounded-full bg-green-500" title={`Low: ${stats?.risk_distribution?.low || 0}`}></span>
                <span className="w-3 h-3 rounded-full bg-yellow-500" title={`Medium: ${stats?.risk_distribution?.medium || 0}`}></span>
                <span className="w-3 h-3 rounded-full bg-orange-500" title={`High: ${stats?.risk_distribution?.high || 0}`}></span>
                <span className="w-3 h-3 rounded-full bg-red-500" title={`Critical: ${stats?.risk_distribution?.critical || 0}`}></span>
              </div>
              <p className="text-sm text-gray-600 mt-1">Risk Distribution</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filter */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input 
                  placeholder="Search JHAs by title, number, or author..." 
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[140px]">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="reviewed">Reviewed</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterRisk} onValueChange={setFilterRisk}>
              <SelectTrigger className="w-[140px]">
                <AlertTriangle className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Risk Level" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Risks</SelectItem>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Templates */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <FileCheck className="w-4 h-4" /> Quick Start Templates
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="basic">
            <TabsList>
              <TabsTrigger value="basic">Basic</TabsTrigger>
              {industryTemplates.map(industry => (
                <TabsTrigger key={industry.id} value={industry.id}>{industry.name}</TabsTrigger>
              ))}
            </TabsList>
            <TabsContent value="basic" className="pt-3">
              <div className="flex flex-wrap gap-2">
                {templates.map((template) => (
                  <Button key={template.id} variant="outline" size="sm" onClick={() => { loadTemplate(template); setShowCreateForm(true); }}>
                    <FileText className="w-4 h-4 mr-2" /> {template.title.replace(' JHA Template', '')}
                  </Button>
                ))}
              </div>
            </TabsContent>
            {industryTemplates.map(industry => (
              <TabsContent key={industry.id} value={industry.id} className="pt-3">
                <div className="flex flex-wrap gap-2">
                  {industry.templates?.map((template) => (
                    <Button key={template.id} variant="outline" size="sm" onClick={() => { loadTemplate(template); setShowCreateForm(true); }}>
                      <FileText className="w-4 h-4 mr-2" /> {template.title}
                    </Button>
                  ))}
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </CardContent>
      </Card>

      {/* JHA List */}
      <div className="space-y-4">
        {filteredJHAs.map((jha) => (
          <Card key={jha.id} className="hover:shadow-lg transition-shadow">
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1 cursor-pointer" onClick={() => setSelectedJHA(jha)}>
                  <div className="flex items-center gap-2 mb-2">
                    <Badge className={getStatusColor(jha.status)}>{jha.status}</Badge>
                    <Badge className={getRiskColor(jha.overall_risk_level)}>{jha.overall_risk_level} risk</Badge>
                    <span className="text-sm text-gray-500">{jha.jha_number}</span>
                    {jha.version > 1 && <Badge variant="outline">v{jha.version}</Badge>}
                  </div>
                  <h3 className="font-semibold text-lg">{jha.title}</h3>
                  <p className="text-gray-600 text-sm mb-2 line-clamp-2">{jha.job_description}</p>
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <span>{jha.total_steps || 0} steps</span>
                    <span>{jha.total_hazards || 0} hazards</span>
                    <span>By: {jha.prepared_by}</span>
                    <span>{new Date(jha.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => exportPDF(jha.id)} title="Export PDF">
                    <Download className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => editJHA(jha)} title="Edit">
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => duplicateJHA(jha.id)} title="Duplicate">
                    <Copy className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setShowDeleteConfirm(jha.id)} title="Delete">
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredJHAs.length === 0 && (
        <Card>
          <CardContent className="text-center py-12">
            <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {jhas.length === 0 ? 'No JHAs Created' : 'No JHAs Match Filters'}
            </h3>
            <p className="text-gray-600 mb-4">
              {jhas.length === 0 
                ? 'Start by creating a Job Hazard Analysis or use a template' 
                : 'Try adjusting your search or filter criteria'}
            </p>
            {jhas.length === 0 && (
              <Button onClick={() => { resetForm(); setShowCreateForm(true); }}>Create JHA</Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Create/Edit JHA Dialog */}
      <Dialog open={showCreateForm} onOpenChange={(open) => { if (!open) resetForm(); setShowCreateForm(open); }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingJHA ? 'Edit Job Hazard Analysis' : 'Create Job Hazard Analysis'}</DialogTitle>
            <DialogDescription>Document job steps, hazards, and controls</DialogDescription>
          </DialogHeader>
          <form onSubmit={editingJHA ? handleUpdateJHA : handleCreateJHA} className="space-y-6">
            <Tabs defaultValue="basic" className="w-full">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="basic">Basic Info</TabsTrigger>
                <TabsTrigger value="steps">Task Steps ({formData.task_steps.length})</TabsTrigger>
                <TabsTrigger value="ai">AI Assist</TabsTrigger>
                <TabsTrigger value="summary">Summary</TabsTrigger>
              </TabsList>

              <TabsContent value="basic" className="space-y-4 pt-4">
                <div>
                  <Label>JHA Title *</Label>
                  <Input value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} required placeholder="e.g., Electrical Panel Maintenance" />
                </div>
                <div>
                  <Label>Job Description *</Label>
                  <Textarea value={formData.job_description} onChange={(e) => setFormData({...formData, job_description: e.target.value})} required rows={3} placeholder="Detailed description of the job/task being analyzed" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Department</Label>
                    <Input value={formData.department} onChange={(e) => setFormData({...formData, department: e.target.value})} placeholder="e.g., Maintenance" />
                  </div>
                  <div>
                    <Label>Location</Label>
                    <Input value={formData.location} onChange={(e) => setFormData({...formData, location: e.target.value})} placeholder="e.g., Building A, Floor 2" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Project Name</Label>
                    <Input value={formData.project_name} onChange={(e) => setFormData({...formData, project_name: e.target.value})} placeholder="Associated project (optional)" />
                  </div>
                  <div>
                    <Label>Prepared By *</Label>
                    <Input value={formData.prepared_by} onChange={(e) => setFormData({...formData, prepared_by: e.target.value})} required placeholder="Your name" />
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="steps" className="space-y-4 pt-4">
                <div className="flex justify-between items-center">
                  <Button type="button" onClick={addTaskStep} variant="outline">
                    <Plus className="w-4 h-4 mr-2" /> Add Task Step
                  </Button>
                  <p className="text-sm text-gray-500">Break down the job into sequential steps</p>
                </div>
                <div className="space-y-4">
                  {formData.task_steps.map((step, stepIndex) => (
                    <Card key={stepIndex} className="border-l-4 border-l-blue-500">
                      <CardHeader className="pb-2">
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-sm flex items-center gap-2">
                            <span className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs">
                              {step.step_number}
                            </span>
                            Step {step.step_number}
                          </CardTitle>
                          <div className="flex gap-1">
                            <Button type="button" variant="ghost" size="sm" onClick={() => toggleStepExpand(stepIndex)}>
                              {expandedSteps[stepIndex] ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                            </Button>
                            <Button type="button" variant="ghost" size="sm" onClick={() => removeTaskStep(stepIndex)}>
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div>
                          <Label>Task Description *</Label>
                          <Input value={step.task_description} onChange={(e) => updateTaskStep(stepIndex, 'task_description', e.target.value)} placeholder="What is being done in this step?" />
                        </div>
                        
                        <div className="border-t pt-3">
                          <div className="flex items-center justify-between mb-2">
                            <Label className="flex items-center gap-2">
                              <AlertTriangle className="w-4 h-4 text-orange-500" />
                              Hazards ({step.hazards.length})
                            </Label>
                            <Button type="button" variant="outline" size="sm" onClick={() => addHazardToStep(stepIndex)}>
                              <Plus className="w-4 h-4 mr-1" /> Add Hazard
                            </Button>
                          </div>
                          
                          {step.hazards.map((hazard, hazardIndex) => (
                            <Card key={hazardIndex} className="mb-2 bg-orange-50 border-orange-200">
                              <CardContent className="p-3 space-y-2">
                                <div className="flex justify-between items-start">
                                  <Label className="text-xs text-orange-700">Hazard #{hazardIndex + 1}</Label>
                                  <Button type="button" variant="ghost" size="sm" onClick={() => removeHazard(stepIndex, hazardIndex)}>
                                    <X className="w-3 h-3" />
                                  </Button>
                                </div>
                                <Input placeholder="Hazard description" value={hazard.hazard} onChange={(e) => updateHazard(stepIndex, hazardIndex, 'hazard', e.target.value)} />
                                <Input placeholder="Potential consequences" value={hazard.consequences} onChange={(e) => updateHazard(stepIndex, hazardIndex, 'consequences', e.target.value)} />
                                <div className="grid grid-cols-2 gap-2">
                                  <Select value={hazard.severity} onValueChange={(v) => updateHazard(stepIndex, hazardIndex, 'severity', v)}>
                                    <SelectTrigger><SelectValue placeholder="Severity" /></SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="low">Low</SelectItem>
                                      <SelectItem value="medium">Medium</SelectItem>
                                      <SelectItem value="high">High</SelectItem>
                                      <SelectItem value="critical">Critical</SelectItem>
                                    </SelectContent>
                                  </Select>
                                  <Select value={hazard.likelihood} onValueChange={(v) => updateHazard(stepIndex, hazardIndex, 'likelihood', v)}>
                                    <SelectTrigger><SelectValue placeholder="Likelihood" /></SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="rare">Rare</SelectItem>
                                      <SelectItem value="unlikely">Unlikely</SelectItem>
                                      <SelectItem value="possible">Possible</SelectItem>
                                      <SelectItem value="likely">Likely</SelectItem>
                                      <SelectItem value="almost_certain">Almost Certain</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                                
                                {/* Controls */}
                                <div className="border-t pt-2 mt-2">
                                  <div className="flex justify-between items-center mb-1">
                                    <Label className="text-xs">Controls</Label>
                                    <Button type="button" variant="ghost" size="sm" onClick={() => addControlToHazard(stepIndex, hazardIndex)}>
                                      <Plus className="w-3 h-3 mr-1" /> Add
                                    </Button>
                                  </div>
                                  {hazard.controls?.map((control, controlIndex) => (
                                    <div key={controlIndex} className="flex gap-2 mb-1">
                                      <Select value={control.type} onValueChange={(v) => updateControl(stepIndex, hazardIndex, controlIndex, 'type', v)}>
                                        <SelectTrigger className="w-[120px]">
                                          <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="elimination">Elimination</SelectItem>
                                          <SelectItem value="substitution">Substitution</SelectItem>
                                          <SelectItem value="engineering">Engineering</SelectItem>
                                          <SelectItem value="administrative">Administrative</SelectItem>
                                          <SelectItem value="ppe">PPE</SelectItem>
                                        </SelectContent>
                                      </Select>
                                      <Input 
                                        className="flex-1" 
                                        placeholder="Control description" 
                                        value={control.description} 
                                        onChange={(e) => updateControl(stepIndex, hazardIndex, controlIndex, 'description', e.target.value)} 
                                      />
                                    </div>
                                  ))}
                                </div>
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
                
                {formData.task_steps.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <Target className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                    <p>No task steps added yet</p>
                    <p className="text-sm">Click "Add Task Step" to begin</p>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="ai" className="space-y-4 pt-4">
                <Card className="bg-gradient-to-r from-blue-50 to-purple-50">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Brain className="w-5 h-5 text-purple-600" />
                      AI Hazard Assistant
                    </CardTitle>
                    <CardDescription>
                      Get AI-powered hazard suggestions based on your job description
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="bg-white p-4 rounded-lg">
                      <Label>Current Job Description</Label>
                      <p className="text-sm text-gray-600 mt-1">
                        {formData.job_description || 'Enter a job description in the Basic Info tab first'}
                      </p>
                    </div>
                    
                    <Button 
                      type="button" 
                      onClick={() => getAiHazardSuggestions(0)}
                      disabled={aiLoading || !formData.job_description}
                      className="w-full bg-purple-600 hover:bg-purple-700"
                    >
                      {aiLoading ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyzing...</>
                      ) : (
                        <><Sparkles className="w-4 h-4 mr-2" /> Get AI Hazard Suggestions</>
                      )}
                    </Button>
                    
                    {aiSuggestions.length > 0 && (
                      <div className="space-y-2">
                        <Label>Suggested Hazards (click to add to a step)</Label>
                        {aiSuggestions.map((suggestion, idx) => (
                          <Card key={idx} className="bg-white hover:bg-purple-50 cursor-pointer transition-colors">
                            <CardContent className="p-3">
                              <div className="flex justify-between items-start">
                                <div className="flex-1">
                                  <p className="font-medium text-sm">{suggestion.hazard}</p>
                                  <p className="text-xs text-gray-600">{suggestion.consequences}</p>
                                  <div className="flex gap-2 mt-1">
                                    <Badge className={getRiskColor(suggestion.severity)} variant="outline">
                                      {suggestion.severity}
                                    </Badge>
                                    <Badge variant="outline">{suggestion.likelihood?.replace('_', ' ')}</Badge>
                                  </div>
                                </div>
                                <div className="flex flex-col gap-1">
                                  {formData.task_steps.map((_, stepIdx) => (
                                    <Button 
                                      key={stepIdx}
                                      type="button"
                                      size="sm"
                                      variant="outline"
                                      onClick={() => applyAiSuggestion(stepIdx, suggestion)}
                                    >
                                      + Step {stepIdx + 1}
                                    </Button>
                                  ))}
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="summary" className="space-y-4 pt-4">
                <div>
                  <Label>Emergency Procedures</Label>
                  <Textarea value={formData.emergency_procedures} onChange={(e) => setFormData({...formData, emergency_procedures: e.target.value})} placeholder="Emergency response procedures for this job" rows={3} />
                </div>
                <div>
                  <Label>PPE Summary</Label>
                  <p className="text-sm text-gray-500 mb-2">PPE required for this job (comma separated)</p>
                  <Input value={formData.ppe_summary.join(', ')} onChange={(e) => setFormData({...formData, ppe_summary: e.target.value.split(',').map(s => s.trim()).filter(Boolean)})} placeholder="Hard hat, Safety glasses, Gloves" />
                </div>
                <div>
                  <Label>Required Training (comma separated)</Label>
                  <Input value={formData.required_training.join(', ')} onChange={(e) => setFormData({...formData, required_training: e.target.value.split(',').map(s => s.trim()).filter(Boolean)})} placeholder="LOTO training, Fall protection, Confined space" />
                </div>
                
                {/* Summary Preview */}
                <Card className="bg-gray-50">
                  <CardHeader>
                    <CardTitle className="text-sm">JHA Summary</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div><span className="font-medium">Total Steps:</span> {formData.task_steps.length}</div>
                      <div><span className="font-medium">Total Hazards:</span> {formData.task_steps.reduce((acc, step) => acc + (step.hazards?.length || 0), 0)}</div>
                      <div><span className="font-medium">PPE Items:</span> {formData.ppe_summary.length}</div>
                      <div><span className="font-medium">Training Required:</span> {formData.required_training.length}</div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>

            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => { resetForm(); setShowCreateForm(false); }}>Cancel</Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                {editingJHA ? 'Update JHA' : 'Create JHA'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* JHA Detail Dialog */}
      <Dialog open={!!selectedJHA} onOpenChange={() => setSelectedJHA(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {selectedJHA && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2">
                  <Badge className={getStatusColor(selectedJHA.status)}>{selectedJHA.status}</Badge>
                  <Badge className={getRiskColor(selectedJHA.overall_risk_level)}>{selectedJHA.overall_risk_level} risk</Badge>
                  {selectedJHA.version > 1 && <Badge variant="outline">Version {selectedJHA.version}</Badge>}
                </div>
                <DialogTitle>{selectedJHA.title}</DialogTitle>
                <DialogDescription>{selectedJHA.jha_number} • {selectedJHA.department} • {selectedJHA.location}</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <p className="text-gray-600">{selectedJHA.job_description}</p>
                
                <div className="flex gap-2 flex-wrap">
                  <Button size="sm" variant="outline" onClick={() => exportPDF(selectedJHA.id)}>
                    <Download className="w-4 h-4 mr-1" /> Export PDF
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => editJHA(selectedJHA)}>
                    <Edit className="w-4 h-4 mr-1" /> Edit
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => duplicateJHA(selectedJHA.id)}>
                    <Copy className="w-4 h-4 mr-1" /> Duplicate
                  </Button>
                </div>
                
                <div className="space-y-3">
                  <h4 className="font-medium">Task Steps</h4>
                  {selectedJHA.task_steps?.map((step, idx) => (
                    <Card key={idx}>
                      <CardContent className="p-3">
                        <div className="flex items-start gap-3">
                          <span className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs flex-shrink-0">
                            {step.step_number}
                          </span>
                          <div className="flex-1">
                            <p className="font-medium">{step.task_description}</p>
                            {step.hazards?.map((hazard, hIdx) => (
                              <div key={hIdx} className={`mt-2 p-2 rounded border ${getRiskColor(hazard.severity)}`}>
                                <div className="flex justify-between items-start">
                                  <div>
                                    <p className="font-medium text-sm">{hazard.hazard}</p>
                                    <p className="text-xs">{hazard.consequences}</p>
                                  </div>
                                  <div className="text-xs">
                                    <Badge variant="outline" className="mr-1">{hazard.severity}</Badge>
                                    <Badge variant="outline">{hazard.likelihood?.replace('_', ' ')}</Badge>
                                  </div>
                                </div>
                                {hazard.controls?.length > 0 && (
                                  <div className="mt-2 pt-2 border-t">
                                    <p className="text-xs font-medium mb-1">Controls:</p>
                                    {hazard.controls.map((control, cIdx) => (
                                      <div key={cIdx} className="text-xs flex items-center gap-1">
                                        <Badge variant="outline" className="text-xs">{control.type}</Badge>
                                        <span>{control.description}</span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
                
                {selectedJHA.ppe_summary?.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-2">Required PPE</h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedJHA.ppe_summary.map((ppe, idx) => (
                        <Badge key={idx} variant="outline"><Shield className="w-3 h-3 mr-1" />{ppe}</Badge>
                      ))}
                    </div>
                  </div>
                )}
                
                {selectedJHA.emergency_procedures && (
                  <div>
                    <h4 className="font-medium mb-2">Emergency Procedures</h4>
                    <p className="text-sm text-gray-600">{selectedJHA.emergency_procedures}</p>
                  </div>
                )}
                
                <div className="pt-4 border-t flex gap-2">
                  {selectedJHA.status !== 'approved' && (
                    <Button className="flex-1 bg-green-600 hover:bg-green-700" onClick={() => approveJHA(selectedJHA.id)}>
                      <CheckCircle className="w-4 h-4 mr-2" /> Approve JHA
                    </Button>
                  )}
                  <Button variant="destructive" onClick={() => setShowDeleteConfirm(selectedJHA.id)}>
                    <Trash2 className="w-4 h-4 mr-2" /> Delete
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!showDeleteConfirm} onOpenChange={() => setShowDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete JHA?</DialogTitle>
            <DialogDescription>
              This action cannot be undone. This will permanently delete the JHA document.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setShowDeleteConfirm(null)}>Cancel</Button>
            <Button variant="destructive" onClick={() => deleteJHA(showDeleteConfirm)}>
              <Trash2 className="w-4 h-4 mr-2" /> Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Risk Matrix Dialog */}
      <Dialog open={showRiskMatrix} onOpenChange={setShowRiskMatrix}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Risk Assessment Matrix</DialogTitle>
            <DialogDescription>Use this matrix to determine risk levels based on severity and likelihood</DialogDescription>
          </DialogHeader>
          {riskMatrix && (
            <div className="space-y-4">
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr>
                      <th className="p-2 border bg-gray-100"></th>
                      {riskMatrix.likelihoods?.map(l => (
                        <th key={l} className="p-2 border bg-gray-100 text-xs capitalize">{l.replace('_', ' ')}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {riskMatrix.severities?.slice().reverse().map(s => (
                      <tr key={s}>
                        <td className="p-2 border bg-gray-100 font-medium text-xs capitalize">{s}</td>
                        {riskMatrix.likelihoods?.map(l => {
                          // Use the new key format: severity_likelihood
                          const numScore = riskMatrix.scores?.[`${s}_${l}`] || 0;
                          let bgColor = 'bg-green-200';
                          if (numScore > 12) bgColor = 'bg-red-300';
                          else if (numScore > 8) bgColor = 'bg-orange-300';
                          else if (numScore > 4) bgColor = 'bg-yellow-200';
                          return (
                            <td key={l} className={`p-2 border text-center font-medium ${bgColor}`}>
                              {numScore}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {riskMatrix.risk_levels?.map(level => (
                  <div key={level.level} className="flex items-center gap-2 text-sm">
                    <span className="w-4 h-4 rounded" style={{ backgroundColor: level.color }}></span>
                    <span className="font-medium capitalize">{level.level}:</span>
                    <span className="text-gray-600">{level.description}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default JHABuilderPage;
